import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import {
  CustomRadio,
  CustomTextField,
} from "../../../../shared/components/inputfields/form-elements";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import useNotification from "../../../../shared/hooks/useNotification";
import { useApi } from "../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import { LoaderComponent } from "../../../../shared/components/loaders/page-loader";
import { Utills } from "../../../../shared/app-configs/utilit";
import { CustomStyledInputLabel } from "../../../../shared/components/form-builder/form-styles";
import { InnerButton } from "../../../../shared/components/buttons/floating-button-container";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import ViewerModalPopup from "../../../../shared/components/modals/viewerModal";
import PdfViewer from "../../../../shared/components/preview/pdf-viewer";
import {
  downloadDocx,
  DownloadDocxOptions,
} from "../../../../shared/components/document/docx-downloader";

const DeclarationByCertificateBody: React.FC<{
  onNextTab: any;
  completionMap: Record<string, boolean>;
}> = ({ onNextTab, completionMap }) => {
  const [outsourced, setOutsourced] = React.useState("");
  const { cbLicensingCode, getTCStandardGOTS, isAdmin, originalAdminCBID } =
    useTransactionCertificateStore();
  // Admin is view-only when viewing another CB; admin on their own CB gets full edit access
  const isViewOnly = isAdmin && cbLicensingCode !== originalAdminCBID;
  const [additionalText, setAdditionalText] = React.useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { notify, showBanner } = useNotification();
  const { TE_ID, formtype } = useParams<string>();
  const hasCreateTc: boolean = formtype === "create-tc";
  const hasSubmitTC: boolean = formtype === "submited-tc";
  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);
  const { executePost, executeGet, loading: pageLoading } = useApi();
  const [outsourcedError, setOutsourcedError] = useState("");
  const [showJson, setShowJson] = useState(true);
  const [showDownload, setShowDownload] = useState(false);
  const [getTcStandardDetails, setTcStandardDetails] = useState<any>([]);
  const [previewOpen, setpreviewOpen] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [tcDetails, setTcDetails] = useState<any>(null);

  const fetchStandardDetails = async () => {
    const response: any = await executeGet(TCAppConfig.GET_TC_BASE_DETAILS, {
      tcId: TE_ID,
    });
    if (response?.data) {
      setTcStandardDetails(response?.data?.tcStandard);
      setTcDetails(response?.data);
    }
  };
  const fetchDetails = async () => {
    try {
      const result = await executeGet(
        TCAppConfig.GET_DECLARATION_SUBMIT_DETAILS,
        { tcId: TE_ID }
      );
      setOutsourced(result?.data.tcOutsourcedToSubcontractor);
      setAdditionalText(result?.data.tcSellerProductDeclarations);
    } catch (ex) {
      notify("Unable to Fetch Details", "error");
    }
  };
  useEffect(() => {
    if (TE_ID) {
      fetchDetails();
      fetchStandardDetails();
    }
  }, []);
  const handleSubmit = async () => {
    try {
      if (!getTCStandardGOTS && !outsourced) {
        setOutsourcedError("Outsourced to Subcontractor is required");
        return; // Stop the submit if validation fails
      } else {
        setOutsourcedError(""); // Clear error if valid
      }
      const payload = {
        tcId: TE_ID,
        tcSellerProductDeclarations: additionalText || "",
        tcOutsourcedToSubcontractor: outsourced || "",
      };
      const result: any = await executePost(
        TCAppConfig.DECLARATION_SUBMIT_DETAILS,
        payload
      );
      // await transactionCertificateService.saveDeclaration(payload)
      if (result.status == 200) {
        notify("Data Saved Successfully", "success");
        setShowDownload(true);
        await onNextTab();
      } else {
        notify("Unable to Save", "error");
      }
      Utills.cl(`payload ${JSON.stringify(payload)}`);
    } catch (e) {
      notify("Unable to Save", "error");
    }
  };

  const handleGenerateJSON = async () => {
    try {
      // Show loading spinner if applicable
      // const tcId = "01JGCHXA3FXEZW29B8HEB50X9J"
      const result = await executeGet(
        TCAppConfig.GET_DECLARATION_JSON_DETAILS,
        {
          tcCbLicensingCode: cbLicensingCode,
          tcId: TE_ID,
        }
      );

      if (result?.status === 200) {
        const jsonData = result.data;

        // Convert JSON data to a Blob
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
          type: "application/json",
        });

        // Create a download link and trigger the download
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `declaration_${TE_ID}.json`; // Set file name dynamically based on `TE_ID`
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        notify("JSON downloaded successfully", "success"); // Show success notification
      } else {
        notify("Failed to generate JSON", "error"); // Show error notification
      }
      // Hide loading spinner
    } catch (error) {
      notify("Error generating JSON", "error");
    }
  };
  const handleDownload = async () => {
    setIsDownloading(true);
    
    let filename = 'tc_download.docx';
    if (tcDetails) {
      const cbCode = cbLicensingCode || 'CB';
      const tcNumber = (tcDetails.tcNo || 'TC').replace(/\s+/g, '_');
      const lastUpdated = tcDetails.tcLastUpdated || '';
      const dateStr = lastUpdated.replace(/-/g, ''); 
      filename = `tc_${cbCode}_${tcNumber}_${dateStr}.docx`;
    }
    
    const options: DownloadDocxOptions = {
      apiUrl: TCAppConfig.TC_DOWNLOAD_DOCX,
      params: {
        tcId: TE_ID ?? "",
        cbCode: cbLicensingCode ?? "",
      },
      headers: {
        // Optional headers if needed
      },
      filename: filename,
    };

    try {
      await downloadDocx(options);
      notify("File Downloaded", "success");
    } catch (error) {
      console.error("Failed to download DOCX:", error);
      notify("Failed to download document", "error");
    } finally {
      setIsDownloading(false);
    }
  };
  const handleTCSubmit = async () => {
    try {
      const result: any = await executePost(
        TCAppConfig.DECLARATION_SUBMIT_DETAILS_NEW,
        { tcId: TE_ID }
      );
      if (result.status == 200) {
        notify("TC Submitted Successfully", "success");
        setIsSubmitted(true);
      } else {
        notify("Unable to Submit", "error");
      }
    } catch (error) {
      console.error("Failed to submit TC:", error);
    }
  };
  const togglepreviewDrawer = () => setpreviewOpen((prev) => !prev);
  return (
    <Box p={4}>
      {pageLoading ? (
        <LoaderComponent />
      ) : (
        <>
          {/* Declaration by Certification Body */}
          <Typography
            variant="body1"
            gutterBottom
            align="left"
            fontWeight="600"
          >
            Declaration by Certification Body:
          </Typography>
          <Typography variant="body1" gutterBottom>
            This is to certify that, based on the relevant documentation
            provided by the seller named in screen "Buyer & Seller Details":
          </Typography>
          <Typography
            variant="body1"
            component="div"
            sx={{
              mt: 1,
              pl: 3,
              "& ul": {
                listStyleType: "disc",
                paddingLeft: 2,
                margin: 0,
              },
              "& li": {
                marginBottom: 1.5,
                fontSize: "1rem",
                lineHeight: 1.5,
                color: "text.primary",
              },
            }}
          >
            <ul>
              <li>
                The raw material(s) for the products as further
                detailed/referred to screen "TC Products Details", "Certified
                Raw Material & Standard Details" and "Gross Shipping Weight",
                "Net Shipping Weight" and "Certified Weight" has/have been
                produced in accordance with the{" "}
                {getTcStandardDetails
                  .map((item: any) => `(${item})`)
                  .join(", ")}
                .
              </li>
              <li>
                The products in "TC Products Details" have been processed in
                accordance with the{" "}
                {getTcStandardDetails
                  .map((item: any) => `(${item})`)
                  .join(", ")}
                . Conformity with the standard is audited and monitored
                systematically under the responsibility of the certification
                body.
              </li>
            </ul>
          </Typography>

          {/* Declarations by Seller of Certified Products */}
          <Typography
            variant="body1"
            gutterBottom
            align="left"
            fontWeight="600"
          >
            Declarations by Seller of Certified Products:
          </Typography>
          <Stack gap={2}>
            <Typography variant="body1" gutterBottom>
              The certified product(s) covered in this certificate have been
              outsourced to a subcontractor:
            </Typography>
            <Stack direction={"row"} alignItems={"center"}>
              <Typography variant="body1" gutterBottom sx={{ mr: 2 }}>
                <Typography component="span" sx={{ color: "#a33b36" }}>
                  {!getTCStandardGOTS && "*"}
                </Typography>
                TC Outsourced to Subcontractor
              </Typography>
              <CustomRadio
                readOnly={hasSubmitTC}
                name="outSource"
                // label="TC Outsourced to Subcontractor"
                value={outsourced}
                onChange={(event) => setOutsourced(event.target.value)}
                options={[
                  { label: "Yes", value: "Y" },
                  { label: "No", value: "N" },
                ]}
              />
            </Stack>
            {TE_ID && (
              <ViewerModalPopup
                open={previewOpen}
                onClose={togglepreviewDrawer}
                title="Preview"
              >
                <div style={{ width: "100%", height: "100vh" }}>
                  <PdfViewer
                    apiUrl={TCAppConfig.TC_PREVIEW}
                    params={{ tcId: TE_ID }}
                  />
                </div>
                {/* Embed the reusable PDF Viewer inside the modal */}
              </ViewerModalPopup>
            )}
            {outsourcedError && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {outsourcedError}
              </Typography>
            )}
          </Stack>

          <CustomTextField
            label="Additional declaration text"
            variant="outlined"
            fullWidth
            multiline
            readOnly={hasSubmitTC}
            rows={3}
            placeholder="Enter Text"
            value={additionalText}
            onChange={(event) => setAdditionalText(event.target.value)}
            sx={{ mb: 4 }}
          />

          {/* Action StyledButtons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 2,
              gap: "0.5rem",
            }}
          >
            <NavLink to="/tc-dashboard">
              <InnerButton variant="contained">Home</InnerButton>
            </NavLink>
            {!hasSubmitTC && !isViewOnly && (
              <InnerButton
                variant="contained"
                onClick={handleSubmit}
                color="primary"
              >
                Save
              </InnerButton>
            )}
            {!isViewOnly && (hasSubmitTC) && (
              <NavLink to={`/tc/${formtype}/Input-tc-component/${TE_ID}`}>
                <InnerButton>Back</InnerButton>
              </NavLink>
            )}
            {/* <Tooltip title={showJson ? "Click Submit to generate JSON" : "Click to generate JSON"}>
              <span>
                <StyledButton
                  disabled={showJson}
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateJSON}
                >
                  Generate JSON
                </StyledButton>
              </span>
            </Tooltip> */}
            {completionMap["declaration-certification-body"] && !isViewOnly && (
              <Tooltip title={"Click to Submit"}>
                <span>
                  <InnerButton onClick={handleTCSubmit}>Submit</InnerButton>
                </span>
              </Tooltip>
            )}
            {completionMap["declaration-certification-body"] && (
              <Tooltip title={"Click to Preview"}>
                <span>
                  <InnerButton onClick={togglepreviewDrawer}>
                    Preview
                  </InnerButton>
                </span>
              </Tooltip>
            )}
              <Tooltip title={"Click to Download"}>
                <span>
                  <InnerButton
                    onClick={handleDownload}
                    disabled={isDownloading}
                      sx={{
                        position: "relative",
                        "& .loading-indicator": {
                          position: "absolute",
                          left: "50%",
                          transform: "translateX(-50%)",
                        },
                      }}
                    >
                      {isDownloading ? (
                        <Box
                          className="loading-indicator"
                          display="flex"
                          alignItems="center"
                        >
                          <CircularProgress size={20} color="inherit" />
                        </Box>
                      ) : (
                        "Download Word"
                    )}
                  </InnerButton>
                </span>
              </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );
};

export default DeclarationByCertificateBody;
